import React, { useState } from 'react';
import { Sparkles, ShoppingBag, X } from 'lucide-react';
import { unstitchedCollection } from '../data/mock';

const UnstitchedCollection = () => {
  const [selectedDesign, setSelectedDesign] = useState(null);

  const inquireDesign = (design) => {
    const message = `Hi, I'm interested in ${unstitchedCollection.name} ${design.name} (${design.color}). Please provide more details.`;
    window.open(`https://wa.me/917006350074?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <section id="collection" className="py-20 bg-gradient-to-br from-amber-50 via-cream-50 to-rose-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Sparkles className="w-8 h-8 text-amber-600" />
            <h2 className="section-title">Unstitched Collection</h2>
            <Sparkles className="w-8 h-8 text-rose-600" />
          </div>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-rose-400 mx-auto mb-6"></div>
          
          {/* Collection Header */}
          <div className="mb-8">
            <h3 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-rose-600 mb-2">
              {unstitchedCollection.name}
            </h3>
            <p className="text-xl text-amber-700 font-semibold mb-4">{unstitchedCollection.volume}</p>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              {unstitchedCollection.description}
            </p>
          </div>
        </div>

        {/* Collection Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {unstitchedCollection.designs.map((design) => (
            <div 
              key={design.id}
              className="collection-card group cursor-pointer"
              onClick={() => setSelectedDesign(design)}
            >
              {/* Design Image Placeholder */}
              <div className="relative w-full h-80 bg-gradient-to-br from-gray-100 to-gray-200 rounded-t-lg overflow-hidden">
                <img 
                  src={design.image}
                  alt={`${design.name} - ${design.color}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
                {/* Hover Overlay */}
                <div className="collection-overlay">
                  <button className="bg-white text-amber-700 px-6 py-2 rounded-full font-semibold hover:bg-amber-700 hover:text-white transition-colors duration-300">
                    View Details
                  </button>
                </div>
              </div>

              {/* Design Info */}
              <div className="p-4 bg-white rounded-b-lg">
                <h4 className="text-xl font-bold text-gray-900 mb-2">{design.name}</h4>
                <p className="text-sm text-amber-600 font-semibold mb-2">{design.color}</p>
                <p className="text-sm text-gray-600 mb-3">{design.pieces} | {design.fabric}</p>
                
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    inquireDesign(design);
                  }}
                  className="w-full py-2 px-4 bg-gradient-to-r from-amber-500 to-rose-500 text-white rounded-full font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105"
                >
                  Inquire Now
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Collection Features */}
        <div className="grid md:grid-cols-3 gap-6 mt-16">
          <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-rose-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">Premium Quality</h4>
            <p className="text-gray-700">100% premium lawn fabric with exquisite craftsmanship</p>
          </div>

          <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-rose-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingBag className="w-8 h-8 text-white" />
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">3-Piece Unstitched</h4>
            <p className="text-gray-700">Complete suit with shirt, dupatta, and trouser fabric</p>
          </div>

          <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-rose-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">Intricate Embroidery</h4>
            <p className="text-gray-700">Hand-crafted embroidery with detailed floral motifs</p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="inline-block p-8 bg-white rounded-2xl shadow-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Visit Our Store to See Full Collection
            </h3>
            <p className="text-lg text-gray-700 mb-6">
              Experience the luxury of our fabrics in person at our Hyderpora Chowk location
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button 
                onClick={() => window.open('https://wa.me/917006350074?text=Hi, I want to see the Ramsha Ghazal Luxury Lawn collection', '_blank')}
                className="cta-button"
              >
                Inquire on WhatsApp
              </button>
              <button 
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-3 bg-gray-100 text-gray-800 font-semibold rounded-full hover:bg-gray-200 transition-colors duration-300"
              >
                Visit Store
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Design Detail Modal */}
      {selectedDesign && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedDesign(null)}
        >
          <div className="bg-white rounded-2xl max-w-2xl w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button 
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition-colors"
              onClick={() => setSelectedDesign(null)}
            >
              <X className="w-8 h-8" />
            </button>

            <h3 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-rose-600 mb-4">
              {selectedDesign.name}
            </h3>

            <div className="space-y-4 mb-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Color:</h4>
                <p className="text-gray-700">{selectedDesign.color}</p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Fabric:</h4>
                <p className="text-gray-700">{selectedDesign.fabric}</p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Pieces:</h4>
                <p className="text-gray-700">{selectedDesign.pieces}</p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Embroidery Details:</h4>
                <p className="text-gray-700">{selectedDesign.embroidery}</p>
              </div>
            </div>

            <button 
              onClick={() => inquireDesign(selectedDesign)}
              className="w-full py-3 px-6 bg-gradient-to-r from-amber-500 to-rose-500 text-white rounded-full font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105"
            >
              Inquire on WhatsApp
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default UnstitchedCollection;
